<template>
  <WdsStack
    v-if="!loading && offeredDate !== ''"
    direction-column
    data-test="compliance-status-widget-offer-status"
  >
    <slot name="action">
      <WdsStack v-if="viewingAsClient" direction-column class="tw-px-2">
        <WdsStack justify-space-between>
          <WdsText>
            {{
              hasRecruiterManagingWorkers
                ? $t('Pending signature')
                : $t("Pending worker's signature")
            }}
          </WdsText>
          <WdsText>
            <div class="tw-font-normal">
              {{ $t('Offer sent on :date', { date: offeredDate }) }}
            </div>
          </WdsText>
        </WdsStack>
        <WdsText secondary>
          <div class="tw-font-normal">
            {{
              $t(
                "The offer becomes final once it's signed, and the hire will then be ready to start.",
              )
            }}
          </div>
        </WdsText>
      </WdsStack>
      <WdsStack v-else direction-column class="tw-px-2">
        <PendingContractAcceptanceAlert :hire-id="hireId" />
      </WdsStack>
    </slot>
    <WdsStack direction-column gap-lg>
      <HireComplianceGateStatus
        :compliances="acceptActive"
        :loading="acceptLoading"
        :hire-id="hireId"
      />
    </WdsStack>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsStack, WdsText } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { useHireRecruiter } from '../composables/useHireRecruiter'
import { graphql } from '@/gql'
import { ComplianceName } from '@/gql/graphql'
import HireComplianceGateStatus from '@/components/Hire/HirePageCompliance/HireComplianceGateStatus.vue'
import PendingContractAcceptanceAlert from '@/components/Hire/PendingContractAcceptanceAlert.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { useComplianceGate } from '@/components/Hire/HirePageCompliance/useComplianceGates'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const props = defineProps<{
  hireId: string
}>()

const { result, loading } = useQuery(
  graphql(`
    query HireOfferedStatus($id: ID!) {
      hire(id: $id) {
        id
        offeredAt
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
  },
)

const { hasRecruiterManagingWorkers, viewerIsSupplier } = useHireRecruiter(props.hireId)
const { hasCompanyCapabilities } = useAccountCapabilities()
const viewingAsClient = computed(() => hasCompanyCapabilities.value && !viewerIsSupplier.value)

const hire = computed(() => result.value?.hire)

const offeredDate = computed(() => {
  if (!hire.value?.offeredAt) return ''
  return useDate(hire.value.offeredAt, 'MMM DD, YYYY').date.value
})

const { active: acceptActive, loading: acceptLoading } = useComplianceGate(
  props.hireId,
  ComplianceName.GateHireAccept,
)
</script>
